import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { EMPTY_FILTERS, buildAudience, hasDistrict, normalisePostcode } from "@/lib/audienceEngine";
import { describeAudience, togglePresetFilters } from "@/data/presets";
import { readUrlState, writeUrlState } from "@/lib/urlState";
import { MIN_CAMPAIGN_QUANTITY, calculateCampaignPrice } from "@/config/pricing";
import { dwellStart, dwellStop, funnel, track } from "@/lib/analytics";

const SilverContext = createContext(null);

const DEFAULT_CAMPAIGN = { quantity: 0, isAll: true, format: "letter", creative: null, angle: null, services: [], message: "" };
// Stages the browser history knows about (define → results → campaign). "analysing" is transient.
const STAGE_ORDER = { define: 0, results: 1, campaign: 2 };

export function SilverProvider({ children }) {
  const initial = useMemo(() => readUrlState(), []);
  const [areas, setAreas] = useState(initial.areas);
  const [filters, setFilters] = useState(initial.filters);
  const [phase, setPhase] = useState(initial.view && initial.areas.length ? initial.view : "define");
  const [campaign, setCampaign] = useState(DEFAULT_CAMPAIGN);
  const [builderMode, setBuilderMode] = useState("build");
  const [freeSample, setFreeSample] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);

  const audience = useMemo(() => buildAudience(areas, filters), [areas, filters]);
  const audienceLabel = useMemo(() => describeAudience(filters), [filters]);

  // URL + history: forward stage moves push an entry so the browser back button steps back a stage.
  const prevPhase = useRef(phase);
  const fromPop = useRef(false);
  const depth = useRef(window.history.state?.silverIndex ?? 0);
  const seq = useRef(depth.current);
  const areasRef = useRef(areas);
  areasRef.current = areas;

  useEffect(() => {
    if (phase === "analysing") return;
    const prev = prevPhase.current === "analysing" ? "define" : prevPhase.current;
    const push = STAGE_ORDER[phase] > STAGE_ORDER[prev] && !fromPop.current;
    fromPop.current = false;
    if (push) {
      seq.current += 1;
      depth.current = seq.current;
    }
    writeUrlState(areas, filters, phase, push ? { silverIndex: seq.current } : null);
    prevPhase.current = phase;
  }, [areas, filters, phase]);

  useEffect(() => {
    const onPop = (e) => {
      depth.current = e.state?.silverIndex ?? 0;
      const { view } = readUrlState();
      fromPop.current = true;
      setPhase(view && areasRef.current.length ? view : "define");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (phase !== "define" && areas.length === 0) setPhase("define");
  }, [areas.length, phase]);

  const minQuantity = Math.min(MIN_CAMPAIGN_QUANTITY, audience.total);
  const selectedQuantity = campaign.isAll
    ? audience.total
    : Math.min(Math.max(campaign.quantity, minQuantity), audience.total);
  const price = useMemo(
    () => calculateCampaignPrice(selectedQuantity, { format: campaign.format, services: campaign.services }),
    [selectedQuantity, campaign.format, campaign.services]
  );

  const addAreas = useCallback(
    (codes) => {
      const added = [];
      const invalid = [];
      const duplicate = [];
      codes.forEach((rawCode) => {
        const code = normalisePostcode(rawCode);
        if (!code) return;
        if (!hasDistrict(code)) invalid.push(code);
        else if (areas.includes(code) || added.includes(code)) duplicate.push(code);
        else added.push(code);
      });
      if (added.length) setAreas((prev) => [...prev, ...added.filter((a) => !prev.includes(a))]);
      return { added, invalid, duplicate };
    },
    [areas]
  );

  const removeArea = useCallback((code) => setAreas((prev) => prev.filter((a) => a !== code)), []);
  const clearAreas = useCallback(() => setAreas([]), []);
  const togglePreset = useCallback((preset) => setFilters((f) => togglePresetFilters(f, preset)), []);
  const resetFilters = useCallback(() => setFilters({ ...EMPTY_FILTERS }), []);
  const updateCampaign = useCallback((patch) => setCampaign((c) => ({ ...c, ...patch })), []);
  const setQuantity = useCallback(
    (q) => setCampaign((c) => ({ ...c, quantity: q, isAll: q >= audience.total })),
    [audience.total]
  );

  // Step back one stage. Uses real history when we pushed the current entry, so browser back stays consistent.
  const goBack = useCallback((fallbackPhase) => {
    if (depth.current > 0) window.history.back();
    else setPhase(fallbackPhase);
  }, []);

  const resetAll = useCallback(() => {
    setAreas([]);
    setFilters({ ...EMPTY_FILTERS });
    setCampaign(DEFAULT_CAMPAIGN);
    setBuilderMode("build");
    setFreeSample(false);
    setSubmission(null);
    setPhase("define");
  }, []);

  // Analytics: funnel milestones and audience choices (no-ops without visitor consent).
  const snap = useRef({});
  snap.current = { audience, audienceLabel, filters };
  const prevAreas = useRef(areas);
  useEffect(() => {
    const p = prevAreas.current;
    areas.filter((a) => !p.includes(a)).forEach((code) => track("audience", "area_added", { code }));
    p.filter((a) => !areas.includes(a)).forEach((code) => track("audience", "area_removed", { code }));
    if (areas.length) funnel("area_added");
    prevAreas.current = areas;
  }, [areas]);
  const lastLabel = useRef(audienceLabel);
  useEffect(() => {
    if (lastLabel.current === audienceLabel) return;
    lastLabel.current = audienceLabel;
    track("audience", "filters_changed", { preset: audienceLabel, filters });
  }, [audienceLabel, filters]);
  const prevStage = useRef(null);
  useEffect(() => {
    if (!window.location.pathname.startsWith("/audience")) return;
    if (prevStage.current) dwellStop(`stage:${prevStage.current}`);
    prevStage.current = phase;
    dwellStart(`stage:${phase}`);
    track("stage", phase);
    if (phase === "analysing") {
      const { audience: a, audienceLabel: label, filters: f } = snap.current;
      funnel("audience_built");
      track("audience", "audience_built", { areas: a.areas, total: a.total, preset: label, filters: f });
    }
    if (phase === "results") funnel("results_viewed");
    if (phase === "campaign") funnel("campaign_started");
  }, [phase]);
  useEffect(() => { if (phase === "campaign" && builderMode === "enquire") funnel("enquiry_viewed"); }, [phase, builderMode]);
  useEffect(() => { if (searchOpen) funnel("search_opened"); }, [searchOpen]);
  const lastFormat = useRef(campaign.format);
  useEffect(() => {
    if (lastFormat.current === campaign.format) return;
    lastFormat.current = campaign.format;
    track("audience", "format_selected", { format: campaign.format });
  }, [campaign.format]);
  const lastSample = useRef(freeSample);
  useEffect(() => {
    if (lastSample.current === freeSample) return;
    lastSample.current = freeSample;
    track("audience", "free_sample", { on: freeSample });
  }, [freeSample]);

  const value = {
    areas, filters, setFilters, phase, setPhase, goBack, audience, audienceLabel,
    campaign, updateCampaign, setQuantity, selectedQuantity, minQuantity, price,
    builderMode, setBuilderMode, freeSample, setFreeSample, submission, setSubmission,
    hoveredGroup, setHoveredGroup, filterDrawerOpen, setFilterDrawerOpen, searchOpen, setSearchOpen, howOpen, setHowOpen,
    addAreas, removeArea, clearAreas, togglePreset, resetFilters, resetAll,
  };

  return <SilverContext.Provider value={value}>{children}</SilverContext.Provider>;
}

export const useSilver = () => useContext(SilverContext);

export const scrollToId = (id, behavior = "smooth") => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior, block: "start" });
};

export const focusPostcodeSearch = () => {
  scrollToId("build");
  setTimeout(() => document.querySelector('[data-testid="postcode-input"]')?.focus({ preventScroll: true }), 700);
};
